/**
 * autoresponderForm.ts
 *
 * The pure rules behind Admin → Auto Responders, kept apart from the
 * components so they can be tested without rendering anything:
 *
 *   - which fields each kind of rule shows (R2, R3);
 *   - when Save is allowed, and what a save sends;
 *   - reading a refused save's per-field sentences;
 *   - the words the page uses, including the activity list's reason words
 *     (R21), which stand in for the codes the server stores.
 *
 * Only type imports from the service: nothing here runs any request code.
 */
import type {
  AutoresponderActivityRow,
  AutoresponderChannel,
  AutoresponderEmailReplyKind,
  AutoresponderEventType,
  AutoresponderNamedChoice,
  AutoresponderRule,
  AutoresponderRulePayload,
} from '@/services/autoresponders.service';

// ─── Words ────────────────────────────────────────────────────────────────

/**
 * Every fixed string the page, the editor and the activity list show. Kept in
 * one place so the copy test can check all of it for words a nurse or
 * front-desk admin would not know.
 */
export const COPY = {
  pageTitle: 'Auto Responders',
  pageSubtitle: 'Rules that reply on their own to a text, a new lead or an email reply, and the replies they sent.',
  rulesTab: 'Rules',
  activityTab: 'Recent activity',
  addRule: 'Add rule',
  editRule: 'Edit rule',
  /** The editor's title on a Locked page, where a rule only opens to read. */
  viewRule: 'Rule',
  saveChanges: 'Save changes',
  cancel: 'Cancel',
  close: 'Close',
  deleteAction: 'Delete',
  editAction: 'Edit',
  rulesEmpty: 'No automatic replies set up yet',
  activityEmpty: 'No automatic replies yet',

  // Editor fields
  nameLabel: 'Name',
  namePlaceholder: 'For example, After-hours text reply',
  eventTypeLabel: 'Answers',
  eventTypePlaceholder: 'Choose what this rule replies to…',
  practiceNumberLabel: 'Practice number',
  practiceNumberAny: 'Any practice number',
  leadSourceLabel: 'Lead sources',
  leadSourceAny: 'Any lead source',
  leadSourceSearch: 'Search lead sources…',
  planTypeLabel: 'Plan types',
  planTypeAny: 'Any plan type',
  planTypeSearch: 'Search plan types…',
  choiceGone: 'one no longer available',
  choiceGoneSuffix: '(no longer available)',
  emailReplyKindLabel: 'Replies to',
  emailReplyKindAny: 'Any email from the practice',
  smsTemplateLabel: 'Text template',
  smsTemplateNone: 'No text',
  emailTemplateLabel: 'Email template',
  emailTemplateNone: 'No email',
  headerFooterLabel: 'Header and footer',
  headerFooterNone: 'None',
  templatePlaceholder: 'Choose a template…',
  templateHint: 'Keep the reply short: let the person know their message arrived and when to expect an answer.',
  afterHoursLabel: 'Only reply outside office hours',
  facilityLabel: 'Facilities',
  facilityPlaceholder: 'Choose the facilities whose hours to follow…',
  facilitySearch: 'Search facilities…',
  facilityNoHoursSuffix: '(no office hours set)',
  repeatLabel: 'Reply to the same person at most once every (hours)',
  activeLabel: 'Turned on',

  // List (column titles as the corp dashboard's Auto Responders list)
  columnName: 'Auto Responder Name',
  columnAnswers: 'Answers',
  columnSends: 'Replies With',
  columnWhen: 'When',
  columnOn: 'On',
  searchPlaceholder: 'Search auto responders…',
  searchLabel: 'Search auto responders',
  answersFilterLabel: 'Filter by what the rule answers',
  answersFilterPlaceholder: 'All types',
  rulesNoMatch: 'No rules match',
  rulesNoMatchHint: 'Try adjusting your search or filter.',
  anyTime: 'Any time',
  outsideHours: 'Outside office hours',
  templateGone: 'template no longer available',
  deleteTitle: 'Delete this rule?',

  // Activity
  activityRuleFilterLabel: 'Rule',
  activityAllRules: 'All rules',
  activityColumnTime: 'Time',
  activityColumnRule: 'Rule',
  activityColumnChannel: 'Sent as',
  activityColumnRecipient: 'To',
  activityColumnResult: 'Result',
  activityColumnReason: 'Reason',
  activityNoRule: 'No rule matched',
  activityDeletedRule: '(deleted)',

  // Errors: each says what went wrong and what to do.
  loadError: 'The automatic reply rules could not be loaded. Try again in a moment, or contact Prospera support if it keeps happening.',
  optionsError: 'The templates and other choices for a rule could not be loaded. Refresh the page, or contact Prospera support if it keeps happening.',
  saveError: 'This rule could not be saved. Try again in a moment, or contact Prospera support if it keeps happening.',
  toggleError: 'This rule could not be turned on or off. Try again in a moment, or contact Prospera support if it keeps happening.',
  deleteError: 'This rule could not be deleted. Try again in a moment, or contact Prospera support if it keeps happening.',
  activityError: 'Recent automatic replies could not be loaded. Try again in a moment, or contact Prospera support if it keeps happening.',
} as const;

/** The server sends these labels with its choices; these cover a row shown before they arrive. */
export const EVENT_TYPE_LABELS: Record<AutoresponderEventType, string> = {
  inbound_sms: 'A text to the practice',
  new_lead: 'A new lead',
  email_reply: 'An email reply',
};

export const EMAIL_REPLY_KIND_LABELS: Record<AutoresponderEmailReplyKind, string> = {
  practice: 'Replies to the practice\'s emails',
  marketing: 'Replies to marketing emails',
};

export const CHANNEL_LABELS: Record<AutoresponderChannel, string> = {
  sms: 'Text',
  email: 'Email',
};

/**
 * What the activity list calls each state. `sending` and `interrupted` are
 * both a stored 'claimed' row, told apart by age (see displayStatus).
 */
export type DisplayStatus = 'sending' | 'interrupted' | 'sent' | 'skipped' | 'failed' | 'unknown';

export const STATUS_LABELS: Record<DisplayStatus, string> = {
  sending: 'Sending',
  interrupted: 'Interrupted',
  sent: 'Sent',
  skipped: 'Not sent',
  failed: 'Failed',
  unknown: 'Unconfirmed',
};

export const STATUS_BADGE: Record<DisplayStatus, 'success' | 'warning' | 'error' | 'info' | 'neutral'> = {
  sending: 'info',
  interrupted: 'warning',
  sent: 'success',
  skipped: 'neutral',
  failed: 'error',
  unknown: 'warning',
};

/** A claimed row this old never finished: the attempt stopped part-way. */
export const INTERRUPTED_AFTER_MINUTES = 5;

/** For a reason code this page has no words for yet. Never shows the code. */
export const ACTIVITY_REASON_GENERIC = 'Not sent for another reason. Contact Prospera support if this keeps happening.';

const INTERRUPTED_REASON = 'Stopped before it finished — it may not have been sent';
const UNCONFIRMED_REASON = 'Couldn\'t confirm the message was delivered';

/**
 * Words for every code in api/app/Services/Autoresponder/AutoresponderReason.php.
 * A code whose meaning depends on the channel carries one sentence per channel.
 * The test reads the PHP file, so a new server code without words here fails it.
 */
export const REASON_TEXT: Record<string, string | Record<AutoresponderChannel, string>> = {
  // The message was not one to answer
  unverified: 'Couldn\'t confirm the text came from that number',
  reserved_keyword: 'The text was a stop or help word',
  marketing_number: 'Sent to the marketing number, which never gets automatic replies',
  confirmation_number: 'Sent to the appointment confirmation number',
  not_clinical_number: 'Sent to a number that isn\'t one of the practice\'s text numbers',
  auto_reply: 'The email was itself an automatic message',
  opt_out_request: 'The email asked to stop getting messages',
  staff_sender: 'The email came from the practice\'s own staff or address',
  sender_mismatch: 'The sender isn\'t the patient\'s email address on file',
  sender_unauthenticated: 'Couldn\'t confirm who sent the email',
  form_own_reply: 'Form sends its own reply',
  spam: 'Marked as spam',
  source_unavailable: 'The patient, lead or message could no longer be found',

  // Choosing a rule
  office_hours: 'During office hours',
  office_hours_unknown: 'None of the rule\'s facilities has office hours set',
  facility_unavailable: 'The rule\'s facilities are turned off or were deleted',
  template_unavailable: 'The rule\'s template is turned off or was deleted',

  // The person on one channel
  no_recipient: {
    sms: 'No mobile number to reply to',
    email: 'No email address to reply to',
  },
  unsupported_number: 'Only US and Canadian numbers get text replies',
  invalid_contact: {
    sms: 'Couldn\'t send — the number isn\'t valid',
    email: 'Couldn\'t send — the email address isn\'t valid',
  },
  relay_address: 'The email address belongs to a form service, not the person',
  opted_out: {
    sms: 'Opted out of texts',
    email: 'Opted out of emails',
  },

  // Limits
  repeat_window: 'Already replied recently',
  hourly_cap: 'The hourly limit on automatic replies was reached',

  // Sending
  sender_unavailable: {
    sms: 'The practice has no text number set up to send from',
    email: 'The practice has no email address set up to send from',
  },
  empty_message: 'The template is empty',
  render_failed: 'Couldn\'t fill in the template',
  log_failed: 'Couldn\'t add it to the patient\'s history, so it wasn\'t sent',
  provider_rejected: {
    sms: 'The phone carrier turned the text down',
    email: 'The receiving mail server turned the email down',
  },
  provider_opt_out: 'The person texted STOP to the practice',
  provider_unconfirmed: UNCONFIRMED_REASON,
};

export interface ReasonContext {
  channel?: AutoresponderChannel;
  /** The rule's repeat limit, when known, for "Already replied in the last N hours". */
  repeatWindowHours?: number | null;
}

/** Plain words for a stored reason code. '' for no reason. */
export function reasonText(code: string, context: ReasonContext = {}): string {
  if (code === '') return '';

  if (code === 'repeat_window' && context.repeatWindowHours && context.repeatWindowHours > 0) {
    return context.repeatWindowHours === 1
      ? 'Already replied in the last hour'
      : `Already replied in the last ${context.repeatWindowHours} hours`;
  }

  const entry = Object.prototype.hasOwnProperty.call(REASON_TEXT, code) ? REASON_TEXT[code] : undefined;
  if (entry === undefined) return ACTIVITY_REASON_GENERIC;
  if (typeof entry === 'string') return entry;
  return entry[context.channel ?? 'sms'] ?? entry.sms;
}

// ─── Activity rows ────────────────────────────────────────────────────────

const PACIFIC_TZ = 'America/Los_Angeles';

/** The current Pacific wall clock, as if it were UTC, in ms. */
function pacificWallClockMs(nowMs: number): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: PACIFIC_TZ,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(nowMs));
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  return Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
}

/** A stored Pacific "YYYY-MM-DD HH:MM:SS", as if it were UTC, in ms; null if unreadable. */
function storedWallClockMs(value: string): number | null {
  const m = value.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/);
  if (!m) return null;
  return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]), Number(m[6] ?? 0));
}

/**
 * The state to show. A 'claimed' row is a reply being sent right now; one
 * still claimed after INTERRUPTED_AFTER_MINUTES stopped part-way and is
 * never retried, so it reads as interrupted. Both clocks are Pacific wall
 * time, compared as-is.
 */
export function displayStatus(row: AutoresponderActivityRow, nowMs: number): DisplayStatus {
  switch (row.status) {
    case 'sent':
    case 'skipped':
    case 'failed':
    case 'unknown':
      return row.status;
    case 'claimed': {
      const created = storedWallClockMs(row.createdAt);
      if (created === null) return 'interrupted';
      const ageMinutes = (pacificWallClockMs(nowMs) - created) / 60000;
      return ageMinutes >= INTERRUPTED_AFTER_MINUTES ? 'interrupted' : 'sending';
    }
    default:
      return 'unknown';
  }
}

/** The reason column for one activity row. */
export function activityReason(
  row: AutoresponderActivityRow,
  nowMs: number,
  context: Omit<ReasonContext, 'channel'> = {},
): string {
  const status = displayStatus(row, nowMs);
  if (status === 'interrupted') return INTERRUPTED_REASON;
  if (status === 'sending' || status === 'sent') return '';
  if (status === 'unknown' && row.reason === '') return UNCONFIRMED_REASON;
  if (row.reason === '') return ACTIVITY_REASON_GENERIC;
  return reasonText(row.reason, { ...context, channel: row.channel });
}

// ─── Editor ───────────────────────────────────────────────────────────────

export interface RuleForm {
  name: string;
  eventType: AutoresponderEventType | '';
  /** '' = any of the practice's numbers. */
  practiceNumber: string;
  /** [] = any. A lead must be in every list the rule sets. */
  leadSourceIds: number[];
  planTypeIds: number[];
  /** '' = either kind of email. */
  emailReplyKind: AutoresponderEmailReplyKind | '';
  smsTemplateId: number | null;
  emailTemplateId: number | null;
  emailHeaderFooterId: number | null;
  outsideOfficeHoursOnly: boolean;
  /** The rule replies only while every one of these is closed. */
  officeHoursFacilityIds: number[];
  /** Text, as typed; parsed on save. */
  repeatWindowHours: string;
  active: boolean;
}

export interface FieldVisibility {
  smsTemplate: boolean;
  emailTemplate: boolean;
  headerFooter: boolean;
  practiceNumber: boolean;
  leadSource: boolean;
  planType: boolean;
  emailReplyKind: boolean;
}

/** Which fields a kind of rule uses (R2, R3). Nothing before a kind is chosen. */
export function visibleFields(eventType: AutoresponderEventType | ''): FieldVisibility {
  switch (eventType) {
    case 'inbound_sms':
      return { smsTemplate: true, emailTemplate: false, headerFooter: false, practiceNumber: true, leadSource: false, planType: false, emailReplyKind: false };
    case 'new_lead':
      return { smsTemplate: true, emailTemplate: true, headerFooter: true, practiceNumber: false, leadSource: true, planType: true, emailReplyKind: false };
    case 'email_reply':
      return { smsTemplate: false, emailTemplate: true, headerFooter: true, practiceNumber: false, leadSource: false, planType: false, emailReplyKind: true };
    default:
      return { smsTemplate: false, emailTemplate: false, headerFooter: false, practiceNumber: false, leadSource: false, planType: false, emailReplyKind: false };
  }
}

export function emptyForm(defaultRepeatWindowHours = 24): RuleForm {
  return {
    name: '',
    eventType: '',
    practiceNumber: '',
    leadSourceIds: [],
    planTypeIds: [],
    emailReplyKind: '',
    smsTemplateId: null,
    emailTemplateId: null,
    emailHeaderFooterId: null,
    outsideOfficeHoursOnly: false,
    officeHoursFacilityIds: [],
    repeatWindowHours: String(defaultRepeatWindowHours),
    active: true,
  };
}

/**
 * A rule's lead sources with names. A server older than the lists sends only
 * the single leadSourceId / leadSourceName, which stands in as a one-item list.
 */
export function savedLeadSources(rule: AutoresponderRule): AutoresponderNamedChoice[] {
  if (rule.leadSources) return rule.leadSources;
  if (rule.leadSourceIds) return rule.leadSourceIds.map((id) => ({ id, name: null }));
  return rule.leadSourceId !== null ? [{ id: rule.leadSourceId, name: rule.leadSourceName }] : [];
}

/**
 * The facilities whose office hours a rule follows, with names. A server
 * older than the list sends only officeHoursFacilityId / officeHoursFacilityName,
 * which stands in as a one-item list.
 */
export function savedFacilities(rule: AutoresponderRule): AutoresponderNamedChoice[] {
  if (rule.officeHoursFacilities) return rule.officeHoursFacilities;
  if (rule.officeHoursFacilityIds) return rule.officeHoursFacilityIds.map((id) => ({ id, name: null }));
  return rule.officeHoursFacilityId !== null
    ? [{ id: rule.officeHoursFacilityId, name: rule.officeHoursFacilityName }]
    : [];
}

export function formFromRule(rule: AutoresponderRule): RuleForm {
  return {
    name: rule.name,
    eventType: rule.eventType,
    practiceNumber: rule.practiceNumber ?? '',
    leadSourceIds: savedLeadSources(rule).map((s) => s.id),
    planTypeIds: [...(rule.planTypeIds ?? [])],
    emailReplyKind: rule.emailReplyKind ?? '',
    smsTemplateId: rule.smsTemplateId,
    emailTemplateId: rule.emailTemplateId,
    emailHeaderFooterId: rule.emailHeaderFooterId,
    outsideOfficeHoursOnly: rule.outsideOfficeHoursOnly,
    officeHoursFacilityIds: savedFacilities(rule).map((f) => f.id),
    repeatWindowHours: String(rule.repeatWindowHours),
    active: rule.active,
  };
}

export interface RepeatWindowLimits {
  min: number;
  max: number;
}

function parseHours(value: string): number | null {
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  return Number(trimmed);
}

/**
 * What stops the form from saving, keyed like the server's field errors.
 * The editor shows only the repeat-limit sentence inline (the rest keep Save
 * disabled until chosen); the server says the same things on a refused save.
 */
export function validateForm(form: RuleForm, limits: RepeatWindowLimits): Record<string, string> {
  const errors: Record<string, string> = {};
  const fields = visibleFields(form.eventType);

  if (form.name.trim() === '') errors.name = 'Give the rule a name.';
  if (form.eventType === '') {
    errors.eventType = 'Choose what the rule answers: a text to the practice, a new lead or an email reply.';
  }

  if (form.eventType === 'inbound_sms' && form.smsTemplateId === null) {
    errors.smsTemplateId = 'Choose the text template this rule sends.';
  }
  if (form.eventType === 'email_reply' && form.emailTemplateId === null) {
    errors.emailTemplateId = 'Choose the email template this rule sends.';
  }
  if (form.eventType === 'new_lead' && form.smsTemplateId === null && form.emailTemplateId === null) {
    errors.smsTemplateId = 'Choose a text template, an email template or both.';
  }
  if (fields.headerFooter && form.emailHeaderFooterId !== null && form.emailTemplateId === null) {
    errors.emailHeaderFooterId = 'A header and footer needs an email template. Choose one, or remove the header and footer.';
  }

  if (form.outsideOfficeHoursOnly && form.officeHoursFacilityIds.length === 0) {
    errors.officeHoursFacilityIds = 'Choose at least one facility whose office hours this rule follows.';
  }

  const hours = parseHours(form.repeatWindowHours);
  if (hours === null || hours < limits.min || hours > limits.max) {
    errors.repeatWindowHours = `Enter a whole number of hours from ${limits.min} to ${limits.max}.`;
  }

  return errors;
}

export function canSave(form: RuleForm, limits: RepeatWindowLimits): boolean {
  return Object.keys(validateForm(form, limits)).length === 0;
}

/**
 * The full rule, every key present. Fields the chosen kind of rule does not
 * use go out cleared: the form keeps them while the admin switches kinds, and
 * the server refuses, say, a text rule that still carries an email template.
 * Sending every key also makes an edit replace, not merge with, what is stored.
 */
export function buildPayload(form: RuleForm): AutoresponderRulePayload {
  const fields = visibleFields(form.eventType);
  const hours = parseHours(form.repeatWindowHours);

  return {
    name: form.name.trim(),
    eventType: form.eventType as AutoresponderEventType,
    practiceNumber: fields.practiceNumber && form.practiceNumber !== '' ? form.practiceNumber : null,
    leadSourceIds: fields.leadSource ? [...form.leadSourceIds] : [],
    planTypeIds: fields.planType ? [...form.planTypeIds] : [],
    emailReplyKind: fields.emailReplyKind && form.emailReplyKind !== '' ? form.emailReplyKind : null,
    smsTemplateId: fields.smsTemplate ? form.smsTemplateId : null,
    emailTemplateId: fields.emailTemplate ? form.emailTemplateId : null,
    emailHeaderFooterId: fields.headerFooter && form.emailTemplateId !== null ? form.emailHeaderFooterId : null,
    outsideOfficeHoursOnly: form.outsideOfficeHoursOnly,
    officeHoursFacilityIds: form.outsideOfficeHoursOnly ? [...form.officeHoursFacilityIds] : [],
    repeatWindowHours: hours ?? 0,
    active: form.active,
  };
}

// ─── Server errors ────────────────────────────────────────────────────────

interface ErrorEnvelope {
  response?: {
    data?: {
      error?: {
        message?: unknown;
        details?: { fields?: unknown };
      };
    };
  };
}

/** The per-field sentences from a refused save, keyed like the payload. */
export function fieldErrorsFromError(err: unknown): Record<string, string> {
  const fields = (err as ErrorEnvelope | null)?.response?.data?.error?.details?.fields;
  if (!fields || typeof fields !== 'object') return {};

  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(fields as Record<string, unknown>)) {
    if (typeof value === 'string' && value.trim() !== '') out[key] = value;
  }
  return out;
}

/** The server's plain-language message, or the fallback when it sent none. */
export function errorMessageFromError(err: unknown, fallback: string): string {
  const message = (err as ErrorEnvelope | null)?.response?.data?.error?.message;
  return typeof message === 'string' && message.trim() !== '' ? message : fallback;
}

// ─── Rule summaries for the list ──────────────────────────────────────────

/** How many names a summary lists before counting the rest. */
const SUMMARY_NAMES = 3;

/** "A", "A or B", "A, B or C", "A, B, C or 3 more" (or the same with "and"). */
function listOf(choices: AutoresponderNamedChoice[], conjunction: 'or' | 'and'): string {
  const names = choices.map((c) => c.name ?? COPY.choiceGone);
  if (names.length > SUMMARY_NAMES) {
    return `${names.slice(0, SUMMARY_NAMES).join(', ')} ${conjunction} ${names.length - SUMMARY_NAMES} more`;
  }
  return names.length > 1
    ? `${names.slice(0, -1).join(', ')} ${conjunction} ${names[names.length - 1]}`
    : (names[0] ?? '');
}

/** A lead may match any one. */
function anyOf(choices: AutoresponderNamedChoice[]): string {
  return listOf(choices, 'or');
}

/** Every facility must be closed. */
function allOf(choices: AutoresponderNamedChoice[]): string {
  return listOf(choices, 'and');
}

/** "A", "A and B", "A, B and C": every name, for a sentence that must name them all. */
function joinAll(names: string[]): string {
  return names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : (names[0] ?? '');
}

/**
 * The note under the facilities picker when chosen facilities have no office
 * hours set. The server leaves those out and follows the others; when none
 * of the chosen ones has hours, the rule never replies. '' when all have hours.
 */
export function facilityNoHoursHint(withoutHours: string[], chosenCount: number): string {
  if (withoutHours.length === 0) return '';
  const names = joinAll(withoutHours);
  const one = withoutHours.length === 1;
  if (withoutHours.length >= chosenCount) {
    return one
      ? `${names} has no office hours set, so this rule would never send. Add its hours on the facility, or choose another.`
      : `${names} have no office hours set, so this rule would never send. Add their hours on the facilities, or choose others.`;
  }
  return one
    ? `${names} has no office hours set, so it is left out. The rule follows the other facilities' hours.`
    : `${names} have no office hours set, so they are left out. The rule follows the other facilities' hours.`;
}

export function answersSummary(rule: AutoresponderRule, eventTypeLabel?: string): string {
  const label = eventTypeLabel ?? EVENT_TYPE_LABELS[rule.eventType] ?? '';
  switch (rule.eventType) {
    case 'inbound_sms':
      return rule.practiceNumberLabel ? `${label} at ${rule.practiceNumberLabel}` : label;
    case 'new_lead': {
      const sources = savedLeadSources(rule);
      const planTypes = rule.planTypes ?? [];
      const from = sources.length > 0 ? `from ${anyOf(sources)}` : '';
      const forTypes = planTypes.length > 0 ? `for ${anyOf(planTypes)}` : '';
      if (from && forTypes) return `${label} ${from}, ${forTypes}`;
      return from || forTypes ? `${label} ${from || forTypes}` : label;
    }
    case 'email_reply':
      return rule.emailReplyKind ? EMAIL_REPLY_KIND_LABELS[rule.emailReplyKind] : label;
    default:
      return label;
  }
}

export function whenSummary(rule: AutoresponderRule): string {
  if (!rule.outsideOfficeHoursOnly) return COPY.anyTime;
  const facilities = savedFacilities(rule);
  // One facility with no name reads as it always has: no "at".
  if (facilities.length === 0 || (facilities.length === 1 && facilities[0].name === null)) {
    return COPY.outsideHours;
  }
  return `${COPY.outsideHours} at ${allOf(facilities)}`;
}

export function repeatSummary(hours: number): string {
  return hours === 1 ? 'Once an hour' : `Once every ${hours} hours`;
}
